import { Injectable, NgZone } from '@angular/core';
import { Vault, Device, DeviceSecurityType, VaultType, BrowserVault, IdentityVaultConfig } from '@ionic-enterprise/identity-vault';
import { Platform } from '@ionic/angular';
import { Device as IonicDevice } from '@ionic-native/device/ngx';
import { ConnectService } from '../connect/connect.service';
import { UrlTree } from '@angular/router';
import { KeyService } from './key.service';
import { lastValueFrom } from 'rxjs';
import * as dayjs from 'dayjs';

const config: IdentityVaultConfig = {
  key: 'au.com.weguide',
  type: VaultType.SecureStorage,
  deviceSecurityType: DeviceSecurityType.None,
  lockAfterBackgrounded: 10000,
  shouldClearVaultAfterTooManyFailedAttempts: true,
  customPasscodeInvalidUnlockAttempts: 5,
  unlockVaultOnLoad: false,
};
const key = 'encryption-key';

export interface VaultServiceState {
  encryptionKey: string;
  isLocked: boolean;
  privacyScreen: boolean;
  lockType: 'None' | 'Native' | 'Passcode';
  canUseBiometrics: boolean;
  canUsePasscode: boolean;
  isEmpty: boolean;
}

export const RESET_LINK_SENT_AT_KEY = 'reset_link_sent_at';

@Injectable({ providedIn: 'root' })
export class VaultService {
  public state: VaultServiceState = {
    encryptionKey: '',
    isLocked: false,
    privacyScreen: false,
    lockType: 'None',
    canUseBiometrics: false,
    canUsePasscode: false,
    isEmpty: true,
  };

  vault: Vault | BrowserVault;
  redirectUrl: UrlTree;

  get isNative() {
    return this.isAndroid || this.isIphone;
  }

  get hasNativeLock() {
    return this.state.canUseBiometrics || this.state.canUsePasscode;
  }

  get isAndroid() {
    return this.device.platform === 'Android';
  }

  get isIphone() {
    return this.device.platform === 'iOS';
  }

  constructor(
    public ngZone: NgZone,
    public platform: Platform,
    public device: IonicDevice,
    public connect: ConnectService,
    private keyService: KeyService
  ) {}

  async init() {
    await this.platform.ready(); // This is required only for Cordova
    this.assignVault();
    // On lock and unlock cant be simulated for ngzone run in biometrics flow
    // istanbul ignore next
    this.vault.onLock(() => {
      this.ngZone.run(() => {
        this.state.isLocked = true;
        this.state.encryptionKey = undefined;
      });
    });
    // istanbul ignore next
    this.vault.onUnlock(() => {
      this.ngZone.run(() => {
        this.state.isLocked = false;
      });
    });

    this.state.isLocked = await this.vault.isLocked();

    this.state.privacyScreen = this.isNative ? await Device.isHideScreenOnBackgroundEnabled() : false;
    this.state.canUseBiometrics = this.isNative ? await Device.isBiometricsEnabled() : false;
    this.state.canUsePasscode = this.isNative ? await Device.isSystemPasscodeSet() : false;
    this.state.isEmpty = await this.vault.isEmpty();
    await this.setLockType();
  }

  // istanbul ignore next
  assignVault() {
    this.vault = this.isNative ? new Vault(config) : new BrowserVault(config);
  }

  async setKey() {
    await this.restoreKey();
    if (!this.state.encryptionKey) {
      const encryptionKey = await this.keyService.get();
      await this.vault.setValue(key, encryptionKey);
      this.state.encryptionKey = encryptionKey;
      this.state.isEmpty = await this.vault.isEmpty();
    }
    return this.state.encryptionKey;
  }

  async restoreKey() {
    const value = await this.vault.getValue(key);
    this.state.encryptionKey = value;
  }

  async lockVault() {
    await this.vault.lock();
    this.state.isLocked = true;
  }

  async unlockVault() {
    await this.vault.unlock();
    this.state.isLocked = false;
    this.clearResetLinkSentAt();
  }

  setPrivacyScreen(enabled: boolean) {
    Device.setHideScreenOnBackground(enabled);
    this.state.privacyScreen = enabled;
  }

  async setLockType() {
    let type: VaultType;
    let deviceSecurityType: DeviceSecurityType;

    switch (this.state.lockType) {
      case 'Native':
        type = VaultType.DeviceSecurity;
        deviceSecurityType = DeviceSecurityType.Both;
        break;

      default:
        type = VaultType.SecureStorage;
        deviceSecurityType = DeviceSecurityType.None;
    }
    await this.vault.updateConfig({
      ...this.vault.config,
      type,
      deviceSecurityType,
    });
  }

  async clearVault() {
    await this.vault.clear();
    this.state.lockType = 'None';
    this.state.encryptionKey = undefined;
    this.state.isEmpty = await this.vault.isEmpty();
  }

  async setPin(pin_code: string) {
    await lastValueFrom(this.connect.create(`/pin_codes`, { pin_code }));
    return this.unlockVault();
  }

  async resetPin(pin_code: string, token: string) {
    await lastValueFrom(this.connect.create(`/pin_codes_reset/create`, { pin_code, pin_code_reset_token: token }));
    return this.unlockVault();
  }

  async sendResetLink() {
    try {
      const result = await lastValueFrom(this.connect.get(`/pin_codes_reset`, `new?platform=${this.isNative ? 'app' : 'browser'}`));
      localStorage.setItem(RESET_LINK_SENT_AT_KEY, dayjs().toISOString());
      return result;
    } catch (error) {
      throw error;
    }
  }

  getResetLinkSentAt() {
    const date = localStorage.getItem(RESET_LINK_SENT_AT_KEY);
    if (date) {
      return dayjs(date);
    }
    return null;
  }

  clearResetLinkSentAt() {
    localStorage.removeItem(RESET_LINK_SENT_AT_KEY);
  }

  async validatePin(pin_code: string) {
    await lastValueFrom(this.connect.create(`/pin_codes/validate`, { pin_code }));
    return this.unlockVault();
  }

  async verifyResetToken(token: string) {
    return await lastValueFrom(this.connect.create(`/pin_codes_reset/verify`, { pin_code_reset_token: token }));
  }

  validateBio() {
    return this.vault.requestBiometricPrompt();
  }
}
